import React from "react";
import { useTranslation } from "react-i18next";

import { formatNumber } from "shared/locale/format";
import { EventTypeEnum } from "shared/types";

import Typography from "features/components/Typography";
import {
  CustomTableToolbar,
  DownloadConfig,
  FilterConfig,
} from "features/ui/MaterialTable";

import GroupToggleButtons from "./GroupToggleButtons";
import SmartGroupSwitch from "./SmartGroupsSwitch";

interface EventsTableToolbarProps {
  // Smart grouping props
  smartGroups?: boolean;
  onSmartGroupsChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  dashboardName?: string;
  smartGroupsAllowed?: boolean;

  // Group toggle props
  groupedCount?: number;
  ungroupedCount?: number;
  showUngroupedEvents?: boolean;
  setShowUngroupedEvents?: (show: boolean) => void;
  isLoading?: boolean;

  // Optional right-side content (e.g., download actions)
  rightContent?: React.ReactNode;
  showCount?: boolean;
  countData?: { count: number };
  customActions?: React.ReactNode;
  eventType?: EventTypeEnum;
  download?: DownloadConfig;
  filters?: FilterConfig;
}

const EventsTableToolbar = ({
  smartGroups,
  onSmartGroupsChange,
  dashboardName,
  groupedCount,
  ungroupedCount,
  showUngroupedEvents,
  setShowUngroupedEvents,
  isLoading,
  rightContent,
  smartGroupsAllowed,
  showCount,
  countData,
  customActions,
  eventType,
  download,
  filters,
}: EventsTableToolbarProps) => {
  const { t } = useTranslation();
  const countLabel =
    eventType === EventTypeEnum.CLAIM
      ? t("common:event_type.claim_one")
      : t("common:event_type.event_one");

  return (
    <CustomTableToolbar
      rightContent={
        <>
          {customActions}
          {rightContent}
        </>
      }
      download={download}
      filters={filters}
      countData={showCount ? countData : undefined}
      countLabel={countLabel}
    >
      <div className="flex flex-row items-center justify-start p-1 gap-4">
        {smartGroupsAllowed && (
          <SmartGroupSwitch
            smartGroups={smartGroups}
            onSmartGroupsChange={onSmartGroupsChange}
            displayName={dashboardName}
          />
        )}
        {smartGroups ? (
          <GroupToggleButtons
            displayNamePlural={dashboardName}
            groupedCount={groupedCount}
            ungroupedCount={ungroupedCount}
            showUngroupedEvents={showUngroupedEvents}
            setShowUngroupedEvents={setShowUngroupedEvents}
            isLoading={isLoading}
          />
        ) : (
          !showCount &&
          dashboardName &&
          countData && (
            <>
              <div className="h-8 w-px bg-gray-400" />
              <Typography variant="h5">
                {formatNumber(countData.count || 0, 0)} {dashboardName}
              </Typography>
            </>
          )
        )}
      </div>
    </CustomTableToolbar>
  );
};

export default EventsTableToolbar;
